import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PostHogProvider } from 'posthog-js/react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { posthog } from '@/lib/posthog';
import { initMatomo } from '@/lib/matomo';
import { initSentry } from '@/lib/sentry';
import { initFaro } from '@/lib/telemetry/faro';
import { startScreensReadiness } from '@/lib/telemetry/screens-readiness';
import { startVersionPolling } from '@/lib/sw-update';
import { getOrMintVisitorId } from '@/lib/visitor-id';
import { installFileCdnS3Fallback } from '@/lib/file-cdn';
import { installTranslationDomGuard } from '@/lib/translation-dom-guard';
import { App } from './App';

// Chrome/Edge page translation re-parents React's text nodes into <font>
// wrappers, so the next commit that removes or reorders one throws NotFoundError
// and the root ErrorBoundary takes the whole portal down (#1422). Must run
// before the first React commit — keep it above everything else.
installTranslationDomGuard();

// Production-only: no-op if VITE_MATOMO_URL / VITE_MATOMO_SITE_ID are not set
initMatomo();

// No-op unless VITE_SENTRY_DSN is set; also registers the FolderItemList error seam.
initSentry();

// Browser error telemetry -> Loki. No-op unless VITE_FARO_URL is set.
initFaro();
startScreensReadiness();

// Poll version.json every 5 min to detect new deploys.
// On version mismatch, next user click triggers a full reload.
startVersionPolling();

// Retry failed file-CDN images/videos once via the S3 copy (scio1 parity). No-op without VITE_FILE_CDN_URL.
installFileCdnS3Fallback();

// OptiGrowth visitor identity: mint/repair the durable os_vid (og_vid_v1 before Oct 2026) on every page
// load (pre-auth, ANY route) so first touch isn't limited to /signUp.
getOrMintVisitorId();

// Cleanup: unregister service workers from previous VitePWA setup.
// Safe to remove after 2026-04-19 (30 days).
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const r of regs) r.unregister();
  });
}
import './globals.css';
// Referral widget styles — ships as a standalone CSS file (CSS Modules, NOT
// auto-injected by the JS bundle), so the consumer must import it. Covers all
// referral surfaces: launcher/modal, /app/s/referral page, signup banner.
import '@optiprime/referral-widget/styles.css';
// Host overrides — render the launcher pill as a secondary action (smaller +
// outlined) so it doesn't twin the OptiBot chat FAB it stacks above. Imported
// AFTER the widget styles so it wins on the cascade.
import '@/features/referral/referral-widget-overrides.css';
// Side-effect: applies saved theme class to <html> before first paint
import '@/stores/theme-store';
// Side-effect: initializes i18next before first render
import '@/lib/i18n';
// Side-effect: registers manifest field-action handlers
import '@/features/smallapp/utils/action-handlers';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');
rootEl.dataset.version = `${__APP_VERSION__}+${__GIT_COMMIT_HASH__}`;
rootEl.dataset.buildTime = __BUILD_TIME__;

createRoot(rootEl).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </PostHogProvider>
  </StrictMode>,
);
